import { api } from './client'
import type { ApiEnvelope, PaginatedResponse } from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T>,
): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: raw.number,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

export interface ConversationStats {
    totalSession: number
    totalSuccess: number
    totalFail: number
}

export interface TopProcedure {
    procedureName: string
    total: number
}

export interface HardQuestion {
    id: string
    createdAt?: string
    updatedAt?: string
    robotName?: string
    question?: string
    tagName?: string
    label?: string
}

export interface HardQuestionExportParams {
    page?: number
    size?: number
    fromDate?: string
    toDate?: string
    unitId?: string
}

export interface HardQuestionTag {
    name: string
    label: string
}

export async function fetchConversationStats(
    params?: { fromDate?: string; toDate?: string; unitId?: string },
    signal?: AbortSignal
): Promise<ConversationStats> {
    try {
        const res = await api.get<ApiEnvelope<ConversationStats>>('/v1/survey/session-info/stat', {
            params,
            signal,
        })
        return res.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy thống kê hội thoại.'
        throw new Error(message)
    }
}

export async function fetchTopProcedures(
    params: { top?: number; unitId?: string; fromDate?: string; toDate?: string },
    signal?: AbortSignal
): Promise<TopProcedure[]> {
    try {
        const res = await api.get<ApiEnvelope<TopProcedure[]>>('/v1/survey/top-procedure', {
            params,
            signal,
        })
        return res.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách thủ tục nổi bật.'
        throw new Error(message)
    }
}

export async function fetchHardQuestions(
    params: { page: number; size: number; fromDate?: string; toDate?: string; unitId?: string },
    signal?: AbortSignal
): Promise<PaginatedResponse<HardQuestion>> {
    try {
        const res = await api.get<ApiEnvelope<SpringPage<HardQuestion>>>('/v1/survey/hard-question', {
            params,
            signal,
        })
        return adaptPage<HardQuestion>(res.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách câu hỏi khó.'
        throw new Error(message)
    }
}

export async function fetchHardQuestionTags(signal?: AbortSignal): Promise<HardQuestionTag[]> {
    try {
        const res = await api.get<ApiEnvelope<HardQuestionTag[]>>('/v1/survey/hard-question/tag', {
            signal,
        })
        return res.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách nhãn câu hỏi.'
        throw new Error(message)
    }
}

export async function assignHardQuestionTag(id: string, tagName: string): Promise<void> {
    try {
        await api.put(`/v1/survey/hard-question/${id}/tag`, null, {
            params: { tagName },
        })
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi gán nhãn câu hỏi.'
        throw new Error(message)
    }
}